import {BlockBase} from './interfaces';

const EDITOR_STYLES = `
.PlaygroundEditorTheme__textLowercase {
  text-transform: lowercase;
}
.PlaygroundEditorTheme__textUppercase {
  text-transform: uppercase;
}
.PlaygroundEditorTheme__textCapitalize {
  text-transform: capitalize;
}
.PlaygroundEditorTheme__textUnderline {
  text-decoration: underline;
}
.PlaygroundEditorTheme__textStrikethrough {
  text-decoration: line-through;
}
.PlaygroundEditorTheme__textUnderlineStrikethrough {
  text-decoration: underline line-through;
}
`;
/**
 * CSS styling service for template blocks.
 * Handles different block types and generates appropriate CSS styles.
 */
export class CssStylingService {
  /**
   * Generate CSS styles based on the current template's blocks
   * @param blocks - Array of blocks in the template
   * @returns Generated CSS string
   */
  generateStyles(blocks: BlockBase<any, any>[]): string {
    const usedBlockTypes: string[] = [];
    let css = '/*### START OF GENERATED STYLES*/\n/*### THIS PART OF STYLES IS GENERATED BY YOURS BLOCK CONFIG - DON\'T CHANGE IF YOU WANT TO KEEP SETTINGS WORKING*/\n';
    css += '\n' + EDITOR_STYLES;

    // Process each block to generate its specific styles
    for (const block of blocks) {
      if (usedBlockTypes.includes(block.type)) {
        // add block types only once
        // continue;
      }
      switch (block.type) {
        case 'container':
          css += this.generateContainerBlockStyles(block);
          break;
        case 'benefitsBlock':
          css += this.generateBenefitsBlockStyles(block);
          break;
        case 'mediaBlock':
          css += this.generateMediaBlockStyles(block);
          break;
        default:
          // For other block types, we can add more cases here in the future
          break;
      }
      usedBlockTypes.push(block.type)
    }
    
    return css + '\n/*### END OF GENERATED STYLES*/\n';
  }

  /**
   * Generate CSS styles for Container blocks with column support
   * @param block - The container block to generate styles for
   * @returns CSS string for the container block
   */
  private generateContainerBlockStyles(block: BlockBase<any, any>): string {
    const { properties } = block;
    
    // Default container styles
    let css = `
      div[data-container-block="${block.id}"] {
        display: flex;
        flex-direction: row;
        width: 100%;
      }
      
      @media (max-width: 768px) {
        div[data-container-block="${block.id}"] {
          flex-direction: column !important;
        }
      }
    `;
    
    // Handle columns if specified
    if (properties.layout) {
      return this.generateColumnStyles(block, properties.layout.length);
    }
    
    return css;
  }

  /**
   * Generate CSS styles for container columns
   * @param block - The container block
   * @param columns - Column configuration
   * @returns CSS string for column styling
   */
  private generateColumnStyles(block: BlockBase<any, any>, columns: number): string {
    // For now we'll just create basic grid-based column support
    const css = `
      div[data-container-block="${block.id}"] {
        display: grid;
        grid-template-columns: repeat(${columns}, 1fr);
        gap: ${this.getGapValue(block.properties.gap)};
      }
      
      @media (max-width: 768px) {
        div[data-container-block="${block.id}"] {
          grid-template-columns: 1fr !important;
          gap: ${this.getGapValue(block.properties.gap, true)} !important;
        }
      }
    `;
    
    return css;
  }

  /**
   * Get the appropriate gap value based on properties
   * @param gap - Gap property from block properties
   * @param isMobile - Whether we're getting mobile gap values
   * @returns CSS gap value string
   */
  private getGapValue(gap?: any, isMobile: boolean = false): string {
    if (gap) {
      // Handle different gap formats
      if (typeof gap === 'string') {
        return gap;
      } else if (typeof gap === 'number') {
        return `${gap}px`;
      }
    }
    
    // Default values
    return isMobile ? '16px' : '24px';
  }

  /**
   * Generate CSS styles for Benefits blocks with column support
   * @param block - The benefits block to generate styles for
   * @returns CSS string for the benefits block
   */
  private generateBenefitsBlockStyles(block: BlockBase<any, any>): string {
    const { properties } = block;
    
    // Default benefit list styles
    let css = `
    div[data-block="${block.id}"] .benefit-list-container {
      width: 100%;
    }
    div[data-block="${block.id}"] .benefit-list {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    div[data-block="${block.id}"] .benefit-item {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
    }
    div[data-block="${block.id}"] .benefit-header {
      display: flex;
      align-items: center;
      cursor: pointer;
      width: 100%;
    }
    
    div[data-block="${block.id}"] .benefit-list[data-has-descriptions="true"] .benefit-item[data-open="true"] .benefit-header::after {
        content: '-';
    }
    
    div[data-block="${block.id}"] .benefit-list[data-has-descriptions="true"] .benefit-item .benefit-header::after {
        content: '+';
        display: flex;
        justify-content: center;
        align-items: center;
        width: 10px;
        height: 32px;
        color: currentColor;
        position: absolute;
        right: 30px;
    }
    
    div[data-block="${block.id}"] .benefit-icon {
      width: 32px;
      height: 32px;
      margin-right: 8px;
    }
    `;
    
    // Handle columns if specified (1 to 3)
    if (properties.columns && properties.columns >= 1 && properties.columns <= 3) {
      css += `
      div[data-block="${block.id}"] .benefit-list {
        display: grid;
        grid-template-columns: repeat(${properties.columns}, 1fr);
        gap: ${this.getGapValue(block.properties.gap)};
      }
      @media (max-width: 768px) {
        div[data-block="${block.id}"] .benefit-list {
          grid-template-columns: 1fr !important;
          gap: ${this.getGapValue(block.properties.gap, true)} !important;
        }
      }
      `;
    }
    
    return css;
  }

  /**
   * Generate CSS styles for Media blocks (including gallery slider)
   * @param block - The media block to generate styles for
   * @returns CSS string for the media block
   */
  private generateMediaBlockStyles(block: BlockBase<any, any>): string {
    const { id } = block;

    // Generate gallery slider styles
    return `
    div[data-block="${id}"] .gallery-slider-container {
      position: relative;
      width: 100%;
      height: 400px;
      overflow: hidden;
    }

    div[data-block="${id}"] .gallery-slider-image {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: opacity 500ms ease-in-out;
    }

    div[data-block="${id}"] .gallery-slider-image.active {
      opacity: 1;
    }

    div[data-block="${id}"] .gallery-slider-image.inactive {
      opacity: 0;
    }

    div[data-block="${id}"] .gallery-slider-indicators {
      position: absolute;
      bottom: 16px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      gap: 8px;
    }

    div[data-block="${id}"] .gallery-slider-indicator {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      transition: background-color 300ms ease-in-out;
    }

    div[data-block="${id}"] .gallery-slider-indicator.active {
      background-color: white;
    }

    div[data-block="${id}"] .gallery-slider-indicator.inactive {
      background-color: rgba(255, 255, 255, 0.5);
    }
    `;
  }
}